import { useState, useRef, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    Plus,
    Trash2,
    Upload,
    Download,
    Search,
    Pencil,
    TableProperties,
    Hash,
    X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useRBAC } from '@/hooks/useRBAC';
import { ApiError, assets as assetsApi, type AssetRow, type AssetUpsertPayload } from '@/lib/api';

type AssetStatus = 'active' | 'decommissioned' | 'in-transit' | 'regrind';
type ConsumableType = 'drill-bit' | 'hammer' | 'drill-rod';

/** Form-shape is UI-local. The backend stores diameter as an int and the site
 *  as a string id; we round-trip via parseDiameter/formatDiameter below. */
interface SiteAssetForm {
    id?: string;
    serialNumber: string;
    type: ConsumableType;
    manufacturer: string;
    diameter: string;
    description: string;
    site: string;
    status: AssetStatus;
    totalMetresDrilled: number;
    notes: string;
    poNumber: string;
}

const TYPE_LABELS: Record<ConsumableType, string> = {
    'drill-bit': 'Drill Bit',
    hammer: 'Hammer',
    'drill-rod': 'Drill Rod',
};

const STATUS_LABELS: Record<AssetStatus, string> = {
    active: 'Active',
    decommissioned: 'Decommissioned',
    'in-transit': 'In Transit',
    regrind: 'Regrind',
};

const STATUS_COLORS: Record<AssetStatus, string> = {
    active: 'bg-green-500/15 text-green-400 border-green-500/30',
    decommissioned: 'bg-red-500/15 text-red-400 border-red-500/30',
    'in-transit': 'bg-blue-500/15 text-blue-400 border-blue-500/30',
    regrind: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
};

const MANUFACTURERS = ['Epiroc', 'Mincon', 'AMS', 'Sandvik', 'Numa', 'Robit'];
const SITES = ['Wodgina', 'Tom Price', 'Newman'];
const BIT_ROD_DIAMETERS = ['89mm', '114mm', '127mm', '140mm', '165mm', '177mm', '203mm', '229mm'];
const HAMMER_DIAMETERS = ['4"', '5"', '6"', '7"', '8"'];

const EMPTY_FORM: SiteAssetForm = {
    serialNumber: '',
    type: 'drill-bit',
    manufacturer: '',
    diameter: '',
    description: '',
    site: '',
    status: 'active',
    totalMetresDrilled: 0,
    notes: '',
    poNumber: '',
};

/** "165mm" → 165; '6"' → 6; "" → null. */
function parseDiameter(s: string): number | null {
    if (!s) return null;
    const n = parseInt(s, 10);
    return Number.isFinite(n) ? n : null;
}

function formatDiameter(n: number | null, type: ConsumableType): string {
    if (n == null) return '';
    return type === 'hammer' ? `${n}"` : `${n}mm`;
}

function rowToForm(r: AssetRow): SiteAssetForm {
    return {
        id: r.id,
        serialNumber: r.serial_number,
        type: r.type,
        manufacturer: r.manufacturer,
        diameter: formatDiameter(r.bit_diameter, r.type),
        description: r.description,
        site: r.site_id ?? '',
        status: r.status,
        totalMetresDrilled: r.total_metres_drilled,
        notes: r.notes,
        poNumber: r.po_number,
    };
}

function formToPayload(f: SiteAssetForm): AssetUpsertPayload {
    return {
        serial_number: f.serialNumber,
        type: f.type,
        manufacturer: f.manufacturer,
        bit_diameter: parseDiameter(f.diameter),
        description: f.description,
        site_id: f.site || null,
        status: f.status,
        total_metres_drilled: f.totalMetresDrilled,
        notes: f.notes,
        po_number: f.poNumber,
    };
}

/* ── CSV helpers ──────────────────────────────────────────────── */

const CSV_HEADERS = [
    'serialNumber',
    'type',
    'manufacturer',
    'diameter',
    'description',
    'site',
    'poNumber',
    'notes',
    'status',
    'totalMetresDrilled',
];

function downloadAssetTemplate() {
    // Intake-focused sample: new consignment stock. status/metres are left at
    // active/0 — they're in-service values the operator app fills in over time.
    const sample: SiteAssetForm[] = [
        {
            serialNumber: 'EP-DTH-40218',
            type: 'drill-bit',
            manufacturer: 'Epiroc',
            diameter: '165mm',
            description: '165mm DTH FF Button Bit',
            site: 'Wodgina',
            poNumber: 'PO-2026-0412',
            status: 'active',
            totalMetresDrilled: 0,
            notes: '',
        },
        {
            serialNumber: 'MC-HAM-8821',
            type: 'hammer',
            manufacturer: 'Mincon',
            diameter: '6"',
            description: '6" DTH Hammer',
            site: 'Wodgina',
            poNumber: 'PO-2026-0412',
            status: 'active',
            totalMetresDrilled: 0,
            notes: '',
        },
        {
            serialNumber: 'AMS-ROD-001',
            type: 'drill-rod',
            manufacturer: 'AMS',
            diameter: '114mm',
            description: '114mm 8m D65 Rod',
            site: 'Tom Price',
            poNumber: 'PO-2026-0418',
            status: 'active',
            totalMetresDrilled: 0,
            notes: '',
        },
    ];
    const rows = [
        CSV_HEADERS.join(','),
        ...sample.map((r) =>
            CSV_HEADERS.map((h) => {
                const v = String(r[h as keyof SiteAssetForm] ?? '');
                return v.includes(',') || v.includes('"') ? `"${v.replace(/"/g, '""')}"` : v;
            }).join(',')
        ),
    ];
    const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'asset-register-template.csv';
    a.click();
    URL.revokeObjectURL(a.href);
}

function exportAssetsCSV(assets: SiteAssetForm[]) {
    const rows = [
        CSV_HEADERS.join(','),
        ...assets.map((r) =>
            CSV_HEADERS.map((h) => {
                const v = String(r[h as keyof SiteAssetForm] ?? '');
                return v.includes(',') || v.includes('"') ? `"${v.replace(/"/g, '""')}"` : v;
            }).join(',')
        ),
    ];
    const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'asset-register.csv';
    a.click();
    URL.revokeObjectURL(a.href);
}

function parseCSVLine(line: string): string[] {
    const result: string[] = [];
    let cur = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') {
            if (!inQuotes && cur === '') {
                inQuotes = true;
            } else if (inQuotes && line[i + 1] === '"') {
                cur += '"';
                i++;
            } else if (inQuotes) {
                inQuotes = false;
            } else {
                cur += ch;
            }
        } else if (ch === ',' && !inQuotes) {
            result.push(cur.trim());
            cur = '';
        } else {
            cur += ch;
        }
    }
    result.push(cur.trim());
    return result;
}

const TYPE_NORMALISE: Record<string, ConsumableType> = {
    'drill-bit': 'drill-bit',
    'drill bit': 'drill-bit',
    drillbit: 'drill-bit',
    bit: 'drill-bit',
    hammer: 'hammer',
    'dth hammer': 'hammer',
    'drill-rod': 'drill-rod',
    'drill rod': 'drill-rod',
    drillrod: 'drill-rod',
    rod: 'drill-rod',
};

function parseAssetsCSV(text: string): { entries: SiteAssetForm[]; errors: string[] } {
    const clean = text.replace(/^\uFEFF/, '');
    const lines = clean.split(/\r?\n|\r/).filter((l) => l.trim());
    if (lines.length < 2) return { entries: [], errors: ['CSV has no data rows.'] };

    const headers = parseCSVLine(lines[0]).map((h) => h.toLowerCase().trim());
    const idx = (name: string) => headers.indexOf(name.toLowerCase());
    const entries: SiteAssetForm[] = [];
    const errors: string[] = [];

    for (let i = 1; i < lines.length; i++) {
        const cols = parseCSVLine(lines[i]);
        const get = (name: string) => (cols[idx(name)] ?? '').trim();

        const serial = get('serialnumber') || get('serial_number') || get('serial');
        if (!serial) {
            errors.push(`Row ${i + 1}: missing serial number`);
            continue;
        }

        const rawType = get('type').toLowerCase();
        const type = TYPE_NORMALISE[rawType];
        if (!type) {
            errors.push(`Row ${i + 1}: unrecognised type "${get('type')}"`);
            continue;
        }

        const rawStatus = get('status').toLowerCase();
        const status: AssetStatus = (
            ['active', 'decommissioned', 'in-transit', 'regrind'].includes(rawStatus)
                ? rawStatus
                : 'active'
        ) as AssetStatus;

        entries.push({
            serialNumber: serial,
            type,
            manufacturer: get('manufacturer'),
            diameter: get('diameter'),
            description: get('description'),
            site: get('site'),
            status,
            totalMetresDrilled:
                parseInt(get('totalmetresdrilled') || get('total_metres_drilled') || '0') || 0,
            notes: get('notes'),
            poNumber: get('ponumber') || get('po_number') || get('po'),
        });
    }
    return { entries, errors };
}

/* ── Page component ───────────────────────────────────────────── */

export default function AssetRegister() {
    const { can } = useRBAC();
    const [form, setForm] = useState<SiteAssetForm>({ ...EMPTY_FORM });
    const [editingId, setEditingId] = useState<string | null>(null);
    const [showUpload, setShowUpload] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [importMessage, setImportMessage] = useState<{ text: string; error: boolean } | null>(
        null
    );
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Filters
    const [filterSite, setFilterSite] = useState<string>('all');
    const [filterType, setFilterType] = useState<string>('all');
    const [filterStatus, setFilterStatus] = useState<string>('all');
    const [searchQuery, setSearchQuery] = useState('');

    // Registered consumables. React Query owns the fetch + cache; mutation
    // handlers below call reload() (= refetch) after a successful write.
    const {
        data: assets = [],
        isLoading: loading,
        error: loadError,
        refetch,
    } = useQuery({
        queryKey: ['site-assets'],
        queryFn: async () => {
            const rows = await assetsApi.list({ limit: 2000 });
            return rows.map(rowToForm);
        },
    });
    const reload = () => refetch();

    const filteredAssets = useMemo(() => {
        let list = assets;
        if (filterSite !== 'all') list = list.filter((a) => a.site === filterSite);
        if (filterType !== 'all') list = list.filter((a) => a.type === filterType);
        if (filterStatus !== 'all') list = list.filter((a) => a.status === filterStatus);
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            list = list.filter(
                (a) =>
                    a.serialNumber.toLowerCase().includes(q) ||
                    a.description.toLowerCase().includes(q) ||
                    a.manufacturer.toLowerCase().includes(q)
            );
        }
        return list;
    }, [assets, filterSite, filterType, filterStatus, searchQuery]);

    const sites = useMemo(
        () => [...new Set(assets.map((a) => a.site))].filter(Boolean).sort(),
        [assets]
    );

    if (!can('manage:org')) {
        return (
            <div className='max-w-2xl mx-auto py-12 text-center'>
                <p className='text-muted-foreground'>
                    You don't have permission to view this page.
                </p>
            </div>
        );
    }

    async function handleAdd() {
        if (!form.serialNumber || !form.site) return;

        // Duplicate check is a client-side nicety. The backend also enforces
        // uniqueness via (org_id, serial_number) + ON CONFLICT upserts.
        if (!editingId && assets.some((a) => a.serialNumber === form.serialNumber)) {
            setImportMessage({
                text: `Serial number "${form.serialNumber}" already exists.`,
                error: true,
            });
            setTimeout(() => setImportMessage(null), 5000);
            return;
        }
        try {
            await assetsApi.upsert(formToPayload(form));
            await reload();
            setForm({ ...EMPTY_FORM });
            setEditingId(null);
        } catch (e) {
            const msg = e instanceof ApiError ? e.message : 'Save failed.';
            setImportMessage({ text: msg, error: true });
            setTimeout(() => setImportMessage(null), 5000);
        }
    }

    function startEdit(asset: SiteAssetForm) {
        setForm({ ...asset });
        setEditingId(asset.id ?? null);
    }

    function cancelEdit() {
        setForm({ ...EMPTY_FORM });
        setEditingId(null);
    }

    async function handleDelete(asset: SiteAssetForm) {
        if (!asset.id) return;
        try {
            await assetsApi.remove(asset.id);
            await reload();
            if (editingId === asset.id) cancelEdit();
        } catch (e) {
            const msg = e instanceof ApiError ? e.message : 'Delete failed.';
            setImportMessage({ text: msg, error: true });
            setTimeout(() => setImportMessage(null), 5000);
        }
    }

    async function handleBulkImport() {
        if (!selectedFile) return;
        const text = await selectedFile.text();
        const { entries, errors } = parseAssetsCSV(text);
        setShowUpload(false);
        setSelectedFile(null);

        if (entries.length === 0) {
            setImportMessage({ text: errors.join(' | ') || 'No valid assets found.', error: true });
            setTimeout(() => setImportMessage(null), 15000);
            return;
        }

        // POST each row — the backend upserts on (org_id, serial_number) so a
        // repeat import overwrites the existing record.
        let ok = 0;
        const failed: string[] = [];
        for (const e of entries) {
            try {
                await assetsApi.upsert(formToPayload(e));
                ok++;
            } catch (err) {
                failed.push(
                    `${e.serialNumber}: ${err instanceof ApiError ? err.message : 'failed'}`
                );
            }
        }
        await reload();
        const msg =
            failed.length > 0
                ? `Imported ${ok} consumable${ok === 1 ? '' : 's'}. ${failed.length} failed: ${failed[0]}`
                : `Imported ${ok} consumable${ok === 1 ? '' : 's'} successfully.${errors.length > 0 ? ` ${errors.length} row(s) skipped.` : ''}`;
        setImportMessage({ text: msg, error: failed.length > 0 });
        setTimeout(() => setImportMessage(null), 15000);
    }

    const totalMetres = filteredAssets.reduce((s, a) => s + a.totalMetresDrilled, 0);

    return (
        <div className='max-w-6xl mx-auto'>
            <div className='mb-6'>
                <h1 className='text-2xl font-bold text-foreground'>Consumable Register</h1>
                <p className='text-muted-foreground'>
                    Record serial numbers from new consignments so operators select verified
                    consumables on the rig — no retyping, and no risk of pairing a serial with the
                    wrong manufacturer. Entries here sync to the operator app for selection.
                </p>
            </div>

            {/* Add / Edit form */}
            <Card className='bg-card border-border mb-6'>
                <CardHeader className='pb-2'>
                    <CardTitle className='text-foreground text-base'>
                        {editingId ? `Edit ${form.serialNumber}` : 'Add Consumables to Register'}
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className='grid grid-cols-2 md:grid-cols-4 gap-3 p-4 rounded-lg border border-border bg-background'>
                        <div className='space-y-1'>
                            <Label className='text-xs text-muted-foreground'>Serial Number</Label>
                            <Input
                                className='h-9 font-mono'
                                placeholder='e.g. EP-DTH-40218'
                                value={form.serialNumber}
                                onChange={(e) =>
                                    setForm((f) => ({ ...f, serialNumber: e.target.value }))
                                }
                                disabled={!!editingId}
                            />
                        </div>

                        <div className='space-y-1'>
                            <Label className='text-xs text-muted-foreground'>Type</Label>
                            <Select
                                value={form.type}
                                onValueChange={(v) =>
                                    setForm((f) => ({ ...f, type: v as ConsumableType }))
                                }
                            >
                                <SelectTrigger className='h-9'>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {Object.entries(TYPE_LABELS).map(([v, l]) => (
                                        <SelectItem key={v} value={v}>
                                            {l}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className='space-y-1'>
                            <Label className='text-xs text-muted-foreground'>Manufacturer</Label>
                            <Select
                                value={form.manufacturer}
                                onValueChange={(v) => setForm((f) => ({ ...f, manufacturer: v }))}
                            >
                                <SelectTrigger className='h-9'>
                                    <SelectValue placeholder='Select...' />
                                </SelectTrigger>
                                <SelectContent>
                                    {MANUFACTURERS.map((m) => (
                                        <SelectItem key={m} value={m}>
                                            {m}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className='space-y-1'>
                            <Label className='text-xs text-muted-foreground'>Diameter</Label>
                            <Select
                                value={form.diameter}
                                onValueChange={(v) => setForm((f) => ({ ...f, diameter: v }))}
                            >
                                <SelectTrigger className='h-9'>
                                    <SelectValue placeholder='Select...' />
                                </SelectTrigger>
                                <SelectContent>
                                    {(form.type === 'hammer'
                                        ? HAMMER_DIAMETERS
                                        : BIT_ROD_DIAMETERS
                                    ).map((d) => (
                                        <SelectItem key={d} value={d}>
                                            {d}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className='space-y-1'>
                            <Label className='text-xs text-muted-foreground'>Description</Label>
                            <Input
                                className='h-9'
                                placeholder='e.g. 165mm DTH FF Button Bit'
                                value={form.description}
                                onChange={(e) =>
                                    setForm((f) => ({ ...f, description: e.target.value }))
                                }
                            />
                        </div>

                        <div className='space-y-1'>
                            <Label className='text-xs text-muted-foreground'>Site</Label>
                            <Select
                                value={form.site}
                                onValueChange={(v) => setForm((f) => ({ ...f, site: v }))}
                            >
                                <SelectTrigger className='h-9'>
                                    <SelectValue placeholder='Select...' />
                                </SelectTrigger>
                                <SelectContent>
                                    {SITES.map((s) => (
                                        <SelectItem key={s} value={s}>
                                            {s}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className='space-y-1'>
                            <Label className='text-xs text-muted-foreground'>PO Number</Label>
                            <Input
                                className='h-9 font-mono'
                                placeholder='e.g. PO-2026-0412'
                                value={form.poNumber}
                                onChange={(e) =>
                                    setForm((f) => ({ ...f, poNumber: e.target.value }))
                                }
                            />
                        </div>

                        <div className='space-y-1 md:col-span-3'>
                            <Label className='text-xs text-muted-foreground'>Notes</Label>
                            <Input
                                className='h-9'
                                placeholder='Optional notes...'
                                value={form.notes}
                                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                            />
                        </div>

                        <div className='flex items-end gap-2'>
                            <Button
                                size='sm'
                                onClick={() => void handleAdd()}
                                disabled={!form.serialNumber || !form.site}
                            >
                                {editingId ? (
                                    <Pencil className='h-4 w-4 mr-2' />
                                ) : (
                                    <Plus className='h-4 w-4 mr-2' />
                                )}
                                {editingId ? 'Update' : 'Add to Register'}
                            </Button>
                            {editingId && (
                                <Button size='sm' variant='ghost' onClick={cancelEdit}>
                                    <X className='h-4 w-4 mr-1' /> Cancel
                                </Button>
                            )}
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Filters + actions bar */}
            <Card className='bg-card border-border'>
                <CardHeader>
                    <div className='flex items-start justify-between gap-4 flex-wrap'>
                        <div>
                            <CardTitle className='text-foreground flex items-center gap-2'>
                                <Hash className='h-5 w-5 text-primary' />
                                Registered Consumables
                                <span className='text-sm font-normal text-muted-foreground ml-2'>
                                    {filteredAssets.length} consumable
                                    {filteredAssets.length !== 1 ? 's' : ''}
                                    {filteredAssets.length !== assets.length &&
                                        ` of ${assets.length}`}
                                    {totalMetres > 0 && ` · ${totalMetres.toLocaleString()}m total`}
                                </span>
                            </CardTitle>
                            {importMessage ? (
                                <p
                                    className={`text-xs mt-2 ${importMessage.error ? 'text-destructive' : 'text-green-500'}`}
                                >
                                    {importMessage.text}
                                </p>
                            ) : loadError ? (
                                <p className='text-xs mt-2 text-destructive'>
                                    {loadError instanceof ApiError
                                        ? loadError.message
                                        : 'Failed to load consumables.'}
                                </p>
                            ) : null}
                        </div>
                        <div className='flex gap-2'>
                            <Button
                                variant='outline'
                                size='sm'
                                onClick={() => exportAssetsCSV(assets)}
                            >
                                <Download className='h-3.5 w-3.5 mr-1.5' /> Export CSV
                            </Button>
                            <Button variant='outline' size='sm' onClick={() => setShowUpload(true)}>
                                <Upload className='h-3.5 w-3.5 mr-1.5' /> Import CSV
                            </Button>
                        </div>
                    </div>

                    {/* Filters */}
                    <div className='flex gap-3 flex-wrap mt-3'>
                        <div className='relative flex-1 min-w-[200px]'>
                            <Search className='absolute left-2.5 top-2 h-4 w-4 text-muted-foreground' />
                            <Input
                                className='h-8 pl-9 text-sm'
                                placeholder='Search serial, description, manufacturer...'
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                        <Select value={filterSite} onValueChange={setFilterSite}>
                            <SelectTrigger className='h-8 w-[140px] text-sm'>
                                <SelectValue placeholder='All Sites' />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value='all'>All Sites</SelectItem>
                                {sites.map((s) => (
                                    <SelectItem key={s} value={s}>
                                        {s}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select value={filterType} onValueChange={setFilterType}>
                            <SelectTrigger className='h-8 w-[130px] text-sm'>
                                <SelectValue placeholder='All Types' />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value='all'>All Types</SelectItem>
                                {Object.entries(TYPE_LABELS).map(([v, l]) => (
                                    <SelectItem key={v} value={v}>
                                        {l}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select value={filterStatus} onValueChange={setFilterStatus}>
                            <SelectTrigger className='h-8 w-[140px] text-sm'>
                                <SelectValue placeholder='All Status' />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value='all'>All Status</SelectItem>
                                {Object.entries(STATUS_LABELS).map(([v, l]) => (
                                    <SelectItem key={v} value={v}>
                                        {l}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </CardHeader>

                <CardContent>
                    {loading ? (
                        <p className='text-sm text-muted-foreground text-center py-8'>
                            Loading assets…
                        </p>
                    ) : filteredAssets.length > 0 ? (
                        <div className='overflow-x-auto'>
                            <table className='w-full text-sm'>
                                <thead>
                                    <tr className='border-b border-border text-muted-foreground text-left'>
                                        <th className='pb-2 pr-4 font-medium'>Serial Number</th>
                                        <th className='pb-2 pr-4 font-medium'>Type</th>
                                        <th className='pb-2 pr-4 font-medium'>Manufacturer</th>
                                        <th className='pb-2 pr-4 font-medium'>Diameter</th>
                                        <th className='pb-2 pr-4 font-medium'>Description</th>
                                        <th className='pb-2 pr-4 font-medium'>Site</th>
                                        <th className='pb-2 pr-4 font-medium'>PO</th>
                                        <th className='pb-2 pr-4 font-medium'>Status</th>
                                        <th className='pb-2 pr-4 font-medium text-right'>Metres</th>
                                        <th className='pb-2 pr-4 font-medium'>Notes</th>
                                        <th className='pb-2 font-medium' />
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredAssets.map((a) => (
                                        <tr
                                            key={a.id ?? a.serialNumber}
                                            className='border-b border-border/50 hover:bg-muted/30'
                                        >
                                            <td className='py-2.5 pr-4 text-foreground font-mono font-medium'>
                                                {a.serialNumber}
                                            </td>
                                            <td className='py-2.5 pr-4 text-foreground'>
                                                {TYPE_LABELS[a.type]}
                                            </td>
                                            <td className='py-2.5 pr-4 text-foreground'>
                                                {a.manufacturer}
                                            </td>
                                            <td className='py-2.5 pr-4 text-foreground'>
                                                {a.diameter}
                                            </td>
                                            <td className='py-2.5 pr-4 text-foreground'>
                                                {a.description}
                                            </td>
                                            <td className='py-2.5 pr-4 text-foreground'>
                                                {a.site}
                                            </td>
                                            <td className='py-2.5 pr-4 text-muted-foreground font-mono text-xs'>
                                                {a.poNumber || '—'}
                                            </td>
                                            {(() => {
                                                // In-service columns (status / metres) only become
                                                // meaningful once the operator app reports usage. Until
                                                // then a freshly-registered consumable shows "New".
                                                const inService =
                                                    a.totalMetresDrilled > 0 ||
                                                    a.status !== 'active';
                                                return (
                                                    <>
                                                        <td className='py-2.5 pr-4'>
                                                            {inService ? (
                                                                <Badge
                                                                    variant='outline'
                                                                    className={`text-xs ${STATUS_COLORS[a.status]}`}
                                                                >
                                                                    {STATUS_LABELS[a.status]}
                                                                </Badge>
                                                            ) : (
                                                                <span className='text-xs text-muted-foreground'>
                                                                    New
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className='py-2.5 pr-4 text-foreground text-right font-medium'>
                                                            {inService ? (
                                                                a.totalMetresDrilled.toLocaleString()
                                                            ) : (
                                                                <span className='text-muted-foreground'>
                                                                    —
                                                                </span>
                                                            )}
                                                        </td>
                                                    </>
                                                );
                                            })()}
                                            <td className='py-2.5 pr-4 text-muted-foreground text-xs max-w-[150px] truncate'>
                                                {a.notes || '—'}
                                            </td>
                                            <td className='py-2.5'>
                                                <div className='flex gap-1'>
                                                    <Button
                                                        variant='ghost'
                                                        size='icon'
                                                        className='h-7 w-7 text-muted-foreground hover:text-primary'
                                                        onClick={() => startEdit(a)}
                                                    >
                                                        <Pencil className='h-3.5 w-3.5' />
                                                    </Button>
                                                    <Button
                                                        variant='ghost'
                                                        size='icon'
                                                        className='h-7 w-7 text-muted-foreground hover:text-destructive'
                                                        onClick={() => void handleDelete(a)}
                                                    >
                                                        <Trash2 className='h-3.5 w-3.5' />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <p className='text-sm text-muted-foreground text-center py-8'>
                            {assets.length === 0
                                ? 'No consumables registered yet. Add one above or import a consignment CSV.'
                                : 'No consumables match the current filters.'}
                        </p>
                    )}
                </CardContent>
            </Card>

            {/* Bulk Upload Dialog */}
            <Dialog
                open={showUpload}
                onOpenChange={(open) => {
                    setShowUpload(open);
                    if (!open) setSelectedFile(null);
                }}
            >
                <DialogContent className='sm:max-w-md'>
                    <DialogHeader>
                        <DialogTitle className='flex items-center gap-2'>
                            <TableProperties className='h-5 w-5 text-primary' />
                            Import Consumable Register
                        </DialogTitle>
                        <p className='text-sm text-muted-foreground'>
                            Upload a consignment CSV to add or update consumables. Existing records
                            with matching serial numbers will be updated.
                        </p>
                    </DialogHeader>

                    <div className='flex items-center justify-between bg-muted/40 rounded-lg px-4 py-3'>
                        <div className='flex items-center gap-2 text-sm text-muted-foreground'>
                            <Download className='h-4 w-4' />
                            Download template with sample data
                        </div>
                        <Button variant='outline' size='sm' onClick={downloadAssetTemplate}>
                            <Download className='h-3.5 w-3.5 mr-1' /> Template
                        </Button>
                    </div>

                    <button
                        type='button'
                        onClick={() => fileInputRef.current?.click()}
                        className='w-full border-2 border-dashed border-border rounded-lg py-10 flex flex-col items-center gap-2 hover:border-primary/50 transition-colors'
                    >
                        <Upload className='h-8 w-8 text-muted-foreground' />
                        <p className='text-sm font-medium text-foreground'>
                            {selectedFile ? selectedFile.name : 'Click to upload CSV file'}
                        </p>
                        <p className='text-xs text-muted-foreground'>.csv files only</p>
                    </button>
                    <input
                        ref={fileInputRef}
                        type='file'
                        accept='.csv'
                        className='hidden'
                        onChange={(e) => {
                            setSelectedFile(e.target.files?.[0] ?? null);
                            e.target.value = '';
                        }}
                    />

                    <div className='flex justify-end gap-2 pt-1'>
                        <Button
                            variant='outline'
                            onClick={() => {
                                setShowUpload(false);
                                setSelectedFile(null);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button onClick={() => void handleBulkImport()} disabled={!selectedFile}>
                            <Upload className='h-4 w-4 mr-1.5' /> Import
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
